import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import './IntegrationApplicationMasterData.css';

interface RoadmapStats {
  totalScheduled: number;
  integrationGroups: number;
  deploymentStatus: {
    needApproval: number;
    approved: number;
    inDevelopment: number;
    deployed: number;
  };
  scheduleStatus: {
    scheduled: number;
    inProgress: number;
    completed: number;
  };
  timeline: {
    earliestStart: string | null;
    latestEnd: string | null;
    totalDurationDays: number;
  };
  plannedMigrations: number;
  scheduledRetirements: number;
}

const HighLevelRoadMap: React.FC = () => {
  const [stats, setStats] = useState<RoadmapStats>({
    totalScheduled: 0,
    integrationGroups: 0,
    deploymentStatus: { needApproval: 0, approved: 0, inDevelopment: 0, deployed: 0 },
    scheduleStatus: { scheduled: 0, inProgress: 0, completed: 0 },
    timeline: { earliestStart: null, latestEnd: null, totalDurationDays: 0 },
    plannedMigrations: 0,
    scheduledRetirements: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRoadmapStats();
  }, []);

  const loadRoadmapStats = async () => {
    try {
      setLoading(true);

      // Fetch roadmap data and deployment statuses
      const [roadmapResponse, deploymentResponse, applicationsData] = await Promise.all([
        fetch('/api/roadmap-schedule/visualization/gantt'),
        fetch('/api/deployment-status'),
        apiService.getApplications().catch(() => [])
      ]);

      const roadmapResult = await roadmapResponse.json();
      let deploymentData: any[] = [];
      try {
        const depResult = await deploymentResponse.json();
        if (Array.isArray(depResult)) {
          deploymentData = depResult;
        }
      } catch (e) {
        // Ignore errors
      }

      // Calculate statistics from roadmap data
      const apps = roadmapResult.success && roadmapResult.data ? roadmapResult.data : [];

      // Merge deployment statuses into apps
      const appsWithStatus = apps.map((app: any) => {
        const depStatus = deploymentData.find(
          (ds: any) => ds.integration_group_id === app.integration_group_id && ds.application_id === app.id
        );
        return {
          ...app,
          deployment_status: depStatus?.deployment_status || undefined
        };
      });

      // Calculate deployment status counts
      const needApproval = appsWithStatus.filter((a: any) => a.deployment_status === 'Need Approval').length;
      const approved = appsWithStatus.filter((a: any) => a.deployment_status === 'Approved').length;
      const inDevelopment = appsWithStatus.filter((a: any) => a.deployment_status === 'In Development').length;
      const deployed = appsWithStatus.filter((a: any) => a.deployment_status === 'Deployed').length;

      // Calculate schedule status counts
      const scheduled = appsWithStatus.filter((a: any) => a.schedule_status === 'Scheduled' || !a.schedule_status).length;
      const inProgress = appsWithStatus.filter((a: any) => a.schedule_status === 'In Progress').length;
      const completed = appsWithStatus.filter((a: any) => a.schedule_status === 'Completed').length;

      // Calculate timeline info
      let earliestStart: string | null = null;
      let latestEnd: string | null = null;
      appsWithStatus.forEach((app: any) => {
        if (app.start_date) {
          if (!earliestStart || app.start_date < earliestStart) {
            earliestStart = app.start_date;
          }
        }
        if (app.end_date) {
          if (!latestEnd || app.end_date > latestEnd) {
            latestEnd = app.end_date;
          }
        }
      });

      let totalDurationDays = 0;
      if (earliestStart && latestEnd) {
        const start = new Date(earliestStart);
        const end = new Date(latestEnd);
        totalDurationDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      }

      // Count unique integration groups
      const uniqueGroups = new Set(appsWithStatus.map((a: any) => a.integration_group_id));

      // Calculate migration stats from applications
      const plannedMigrations = applicationsData.filter((app: any) =>
        app.recommendation === 'Re-Platform' || app.recommendation === 'Re-Design'
      ).length;
      const scheduledRetirements = applicationsData.filter((app: any) =>
        app.recommendation === 'Retire'
      ).length;

      setStats({
        totalScheduled: appsWithStatus.length,
        integrationGroups: uniqueGroups.size,
        deploymentStatus: { needApproval, approved, inDevelopment, deployed },
        scheduleStatus: { scheduled, inProgress, completed },
        timeline: { earliestStart, latestEnd, totalDurationDays },
        plannedMigrations,
        scheduledRetirements
      });
    } catch (error) {
      console.error('Error loading roadmap stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">High Level RoadMap</h1>
            <p className="page-header__subtitle">
              Strategic roadmap for application portfolio transformation and rationalization
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Container - Integration Framework Style */}
      <div className="master-data-container">

        {/* Roadmap Overview Statistics */}
        <div className="md-stats-grid">
          <div className="md-card md-card--elevated">
            <div className="md-card-content">
              <div className="md-stat-primary">{loading ? '...' : stats.totalScheduled}</div>
              <div className="md-stat-label" style={{ fontSize: '12px' }}>Total Scheduled</div>
            </div>
          </div>

          <div className="md-card md-card--elevated">
            <div className="md-card-content">
              <div className="md-stat-primary" style={{ color: '#3b82f6' }}>
                {loading ? '...' : stats.integrationGroups}
              </div>
              <div className="md-stat-label" style={{ fontSize: '12px' }}>Integration Groups</div>
            </div>
          </div>

          <div className="md-card md-card--elevated">
            <div className="md-card-content">
              <div className="md-stat-primary" style={{ color: '#10b981' }}>
                {loading ? '...' : stats.deploymentStatus.deployed}
              </div>
              <div className="md-stat-label" style={{ fontSize: '12px' }}>Deployed</div>
            </div>
          </div>

          <div className="md-card md-card--elevated">
            <div className="md-card-content">
              <div className="md-stat-primary" style={{ color: '#8b5cf6' }}>
                {loading ? '...' : stats.deploymentStatus.inDevelopment}
              </div>
              <div className="md-stat-label" style={{ fontSize: '12px' }}>In Development</div>
            </div>
          </div>

        </div>

        {/* Deployment Status Distribution */}
        <div className="md-card">
          <div className="md-card-content">
            <h2 style={{ marginBottom: '24px', color: '#1976d2' }}>Deployment Status Distribution</h2>
            <div className="md-stats-grid">
              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#f59e0b' }}>
                    {loading ? '...' : stats.deploymentStatus.needApproval}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Need Approval</div>
                </div>
              </div>

              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#3b82f6' }}>
                    {loading ? '...' : stats.deploymentStatus.approved}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Approved</div>
                </div>
              </div>

              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#8b5cf6' }}>
                    {loading ? '...' : stats.deploymentStatus.inDevelopment}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>In Development</div>
                </div>
              </div>

              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#10b981' }}>
                    {loading ? '...' : stats.deploymentStatus.deployed}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Deployed</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Schedule Status Overview */}
        <div className="md-card">
          <div className="md-card-content">
            <h2 style={{ marginBottom: '24px', color: '#1976d2' }}>Schedule Status Overview</h2>
            <div className="md-stats-grid">
              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#3b82f6' }}>
                    {loading ? '...' : stats.scheduleStatus.scheduled}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Scheduled</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {stats.totalScheduled > 0
                      ? `${Math.round((stats.scheduleStatus.scheduled / stats.totalScheduled) * 100)}% of total`
                      : '0%'}
                  </div>
                </div>
              </div>

              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#f59e0b' }}>
                    {loading ? '...' : stats.scheduleStatus.inProgress}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>In Progress</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {stats.totalScheduled > 0
                      ? `${Math.round((stats.scheduleStatus.inProgress / stats.totalScheduled) * 100)}% of total`
                      : '0%'}
                  </div>
                </div>
              </div>

              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#10b981' }}>
                    {loading ? '...' : stats.scheduleStatus.completed}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Completed</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {stats.totalScheduled > 0
                      ? `${Math.round((stats.scheduleStatus.completed / stats.totalScheduled) * 100)}% of total`
                      : '0%'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Information */}
        <div className="md-card">
          <div className="md-card-content">
            <h2 style={{ marginBottom: '24px', color: '#1976d2' }}>Timeline Overview</h2>
            <div className="md-stats-grid">
              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#3b82f6', fontSize: '18px' }}>
                    {loading ? '...' : formatDate(stats.timeline.earliestStart)}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Earliest Start Date</div>
                </div>
              </div>

              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#dc2626', fontSize: '18px' }}>
                    {loading ? '...' : formatDate(stats.timeline.latestEnd)}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Latest End Date</div>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default HighLevelRoadMap;